import { CdkDragDrop, moveItemInArray, transferArrayItem } from '@angular/cdk/drag-drop';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Store } from '@ngxs/store';
import _ from 'lodash';
import { combineLatest, Observable, Subject } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';
import { ISpecificBrickSettingsComponent } from 'src/app/components/dashboard/brick-settings/brick-specific-type-settings/ISpecificBrickSettingsComponent';
import { DirectoryColumn } from 'src/app/samwebDescription/samweb/directory/directory-column';
import {
   ChangeShowAvatar,
   ChangeShowFieldNames,
   ChangeVisibleFields,
} from 'src/app/store/dashboard/brick-wall/brick-settings/specific-brick-settings/directory-details-brick-settings/directory-details-settings.action';
import { DirectoryDetailsSettingState } from 'src/app/store/dashboard/brick-wall/brick-settings/specific-brick-settings/directory-details-brick-settings/directory-details-settings.state';
import { DirectoryStructureState } from 'src/app/store/samwin/directory/directory-structure/directory-structure.state';

@Component({
   selector: 'samweb-directory-details-setting',
   templateUrl: './directory-details-setting.component.html',
   styleUrls: ['./directory-details-setting.component.scss'],
})
export class DirectoryDetailsSettingComponent implements OnInit, OnDestroy, ISpecificBrickSettingsComponent {
   brickInstanceId: string;

   public showAvatar$: Observable<boolean>;
   public showFieldNames$: Observable<boolean>;

   public availableColumns$: Observable<DirectoryColumn[]>;
   public hiddenColumns$: Observable<DirectoryColumn[]>;
   public visibleColumns$: Observable<string[]>;
   private destroy$ = new Subject();

   public visibleFields = [];
   public hiddenFields = [];

   constructor(private store: Store, private translationService: TranslateService) {}

   transform(directoryColumnTyp: DirectoryColumn): string {
      return this.store.selectSnapshot(DirectoryStructureState.columnTitleForDirectoryColumn)(directoryColumnTyp, this.translationService);
   }

   ngOnInit(): void {
      this.showAvatar$ = this.store.select(DirectoryDetailsSettingState.getShowAvatar).pipe(map((fn) => fn(this.brickInstanceId)));
      this.showFieldNames$ = this.store.select(DirectoryDetailsSettingState.getShowFieldNames).pipe(map((fn) => fn(this.brickInstanceId)));
      this.visibleColumns$ = this.store.select(DirectoryDetailsSettingState.getVisibleFields).pipe(map((fn) => fn(this.brickInstanceId)));

      const extraColumns$: DirectoryColumn[] = [];
      extraColumns$.push(new DirectoryColumn('APPOINTMENTS', '', 'APPOINTMENTS'));
      extraColumns$.push(new DirectoryColumn('PRESENCE_STATUS', '', 'PRESENCE_STATUS'));
      extraColumns$.push(new DirectoryColumn('PRESENCE_STATUS_FORWARD', '', 'PRESENCE_STATUS_FORWARD'));
      extraColumns$.push(new DirectoryColumn('PRESENCE_STATUS_NOTE', '', 'PRESENCE_STATUS_NOTE'));
      extraColumns$.push(new DirectoryColumn('MOBILE_STATUS', '', 'MOBILE_STATUS'));
      extraColumns$.push(new DirectoryColumn('LINE_STATUS', '', 'LINE_STATUS'));

      const ordered$ = this.store.select(DirectoryStructureState.availableDirectoryColumns).pipe(
         map((f: DirectoryColumn[]) => {
            f.filter((dir) => dir.directoryColumnKey !== 'tbl_org_00_fld_orgnum' && dir.directoryColumnKey !== 'tbl_org_00_fld_orgnam');
            return _(f).concat(extraColumns$).value();
         })
      );

      this.availableColumns$ = combineLatest([this.visibleColumns$, ordered$]).pipe(
         map(([visibleColumns, availableColumns]) => {
            return _(availableColumns)
               .filter((f) => visibleColumns.indexOf(f.directoryColumnKey) > -1)
               .orderBy((f) => visibleColumns.indexOf(f.directoryColumnKey))
               .value();
         })
      );

      this.hiddenColumns$ = combineLatest([this.visibleColumns$, ordered$]).pipe(
         map(([visibleColumns, availableColumns]) => {
            return _(availableColumns)
               .filter((f) => visibleColumns.indexOf(f.directoryColumnKey) < 0)
               .orderBy((f) => this.transform(f))
               .value();
         })
      );

      this.availableColumns$.pipe(takeUntil(this.destroy$)).subscribe((columns) => {
         this.visibleFields = columns;
      });

      this.hiddenColumns$.pipe(takeUntil(this.destroy$)).subscribe((columns) => {
         this.hiddenFields = columns;
      });
   }

   drop(event: CdkDragDrop<string[]>) {
      if (event.previousContainer === event.container) {
         moveItemInArray(event.container.data, event.previousIndex, event.currentIndex);
      } else {
         transferArrayItem(event.previousContainer.data, event.container.data, event.previousIndex, event.currentIndex);
      }
      const fields = <DirectoryColumn[]>this.visibleFields;
      const newVisibleFields = fields.map((f) => {
         const column = <DirectoryColumn>f;
         return column.directoryColumnKey;
      });
      this.store.dispatch(new ChangeVisibleFields(this.brickInstanceId, newVisibleFields));
   }

   ngOnDestroy(): void {
      this.destroy$.next();
      this.destroy$.complete();
   }

   changeShowAvatar(showAvatar: boolean) {
      this.store.dispatch(new ChangeShowAvatar(this.brickInstanceId, showAvatar));
   }

   changeShowFieldNames(showFieldNames: boolean) {
      this.store.dispatch(new ChangeShowFieldNames(this.brickInstanceId, showFieldNames));
   }
}
